import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth.css';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Business details
    business_name: '',
    business_email: '',
    business_phone: '',
    business_address: '',
    
    // Admin details
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    confirm_password: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: null
      });
    }
    // Clear API error when user types
    if (apiError) setApiError('');
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
    if (!formData.business_email.trim()) newErrors.business_email = 'Business email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.business_email)) {
      newErrors.business_email = 'Email is invalid';
    }
    if (!formData.business_phone.trim()) newErrors.business_phone = 'Business phone is required';
    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    return newErrors;
  };

  const handleNext = () => {
    const stepErrors = step === 1 ? validateStep1() : validateStep2();
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const stepErrors = validateStep2();
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    setLoading(true);
    setApiError('');
    
    // Prepare data for API
    const registrationData = {
      // Business details
      business_name: formData.business_name,
      business_email: formData.business_email,
      business_phone: formData.business_phone,
      business_address: formData.business_address || '',
      
      // Admin details
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      username: formData.username,
      password: formData.password,
      phone: formData.phone || formData.business_phone // Use business phone if admin phone not provided
    };

    console.log('Submitting registration data:', registrationData);
    
    const result = await register(registrationData);
    console.log('Registration result:', result);
    
    setLoading(false);

    if (result.success) {
      navigate('/login', { 
        state: { message: 'Registration successful! Please check your email to verify your account.' }
      });
    } else {
      setApiError(result.error || 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="auth-split-container">
      <div className="auth-banner register-banner">
        <div className="banner-content">
          <h2>Grow With Us</h2>
          <p>Join hundreds of businesses managing their sales and inventory smartly.</p>
        </div>
      </div>
      <div className="auth-form-side">
        <div className="auth-card register-card">
          <div className="auth-header">
            <div className="auth-logo">🛍️</div>
            <h1>Register Your Business</h1>
            <p>Start your 14-day free trial</p>
          </div>

          {apiError && (
            <div className="error-alert">
              {apiError}
            </div>
          )}

          <div className="progress-steps">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Business Info</span>
            </div>
            <div className={`step-line ${step >= 2 ? 'active' : ''}`}></div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Admin Account</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {step === 1 && (
              <>
                <div className="form-group">
                  <label htmlFor="business_name">Business Name *</label>
                  <input
                    type="text"
                    id="business_name"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    placeholder="Enter your business name"
                    className={errors.business_name ? 'error' : ''}
                  />
                  {errors.business_name && (
                    <span className="error-message">{errors.business_name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="business_email">Business Email *</label>
                  <input
                    type="email"
                    id="business_email"
                    name="business_email"
                    value={formData.business_email}
                    onChange={handleChange}
                    placeholder="Enter business email"
                    className={errors.business_email ? 'error' : ''}
                  />
                  {errors.business_email && (
                    <span className="error-message">{errors.business_email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="business_phone">Business Phone *</label>
                  <input
                    type="tel"
                    id="business_phone"
                    name="business_phone"
                    value={formData.business_phone}
                    onChange={handleChange}
                    placeholder="Enter business phone"
                    className={errors.business_phone ? 'error' : ''}
                  />
                  {errors.business_phone && (
                    <span className="error-message">{errors.business_phone}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="business_address">Business Address</label>
                  <textarea
                    id="business_address"
                    name="business_address"
                    value={formData.business_address}
                    onChange={handleChange}
                    placeholder="Enter business address"
                    rows="3"
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="first_name">First Name *</label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="First name"
                      className={errors.first_name ? 'error' : ''}
                    />
                    {errors.first_name && (
                      <span className="error-message">{errors.first_name}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="last_name">Last Name *</label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Last name"
                      className={errors.last_name ? 'error' : ''}
                    />
                    {errors.last_name && (
                      <span className="error-message">{errors.last_name}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Choose a username"
                    className={errors.username ? 'error' : ''}
                  />
                  {errors.username && (
                    <span className="error-message">{errors.username}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password"
                    className={errors.password ? 'error' : ''}
                  />
                  {errors.password && (
                    <span className="error-message">{errors.password}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirm_password">Confirm Password *</label>
                  <input
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    className={errors.confirm_password ? 'error' : ''}
                  />
                  {errors.confirm_password && (
                    <span className="error-message">{errors.confirm_password}</span>
                  )}
                </div>
              </>
            )}

            <div className="form-actions">
              {step === 2 && (
                <button 
                  type="button" 
                  onClick={handleBack}
                  className="secondary-button"
                >
                  Back
                </button>
              )}
              
              {step === 1 ? (
                <button 
                  type="button" 
                  onClick={handleNext}
                  className="auth-button"
                >
                  Continue
                </button>
              ) : (
                <button 
                  type="submit" 
                  className="auth-button"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Register Business'}
                </button>
              )}
            </div>
          </form>

          <div className="auth-footer">
            <p>
              Already have an account?{' '}
              <Link to="/login">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;